import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen,
  Clock,
  Play,
  Award,
  TrendingUp,
  Calendar,
  MessageSquare,
  ArrowRight,
  User,
} from "lucide-react";
import axios from "axios";

const DashboardPage = () => {
  const { user, token } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const response = await axios.get(`${API}/enrollments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEnrollments(response.data);
      } catch (error) {
        console.error("Error fetching enrollments:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEnrollments();
  }, [token]);

  const stats = [
    { label: "Courses Enrolled", value: enrollments.length, icon: BookOpen },
    { label: "Hours Learned", value: Math.floor(enrollments.length * 12), icon: Clock },
    { label: "Certificates", value: 0, icon: Award },
  ];

  return (
    <Layout>
      {/* Header */}
      <section className="bg-slate-900 text-white py-12" data-testid="dashboard-header">
        <div className="container-padding">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-lime-500 flex items-center justify-center text-slate-900 text-2xl font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="font-heading text-2xl font-light">Welcome back, {user?.name}!</h1>
                <p className="text-slate-400">{user?.email}</p>
              </div>
            </div>
            <Link to="/courses" data-testid="browse-courses-btn">
              <Button className="bg-lime-500 text-slate-900 hover:bg-lime-400 rounded-full">
                Browse Courses
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="dashboard-stats">
        <div className="container-padding">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="flex items-center gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <stat.icon className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-slate-900">{stat.value}</p>
                  <p className="text-sm text-slate-500">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding" data-testid="dashboard-content">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* My Courses */}
            <div className="lg:col-span-2">
              <h2 className="heading-3 mb-6">My Courses</h2>
              
              {loading ? (
                <div className="space-y-4">
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-100 p-6 animate-pulse">
                      <div className="flex gap-6">
                        <div className="w-32 h-24 bg-slate-200 rounded-lg" />
                        <div className="flex-1 space-y-3">
                          <div className="h-5 bg-slate-200 rounded w-3/4" />
                          <div className="h-4 bg-slate-200 rounded w-1/2" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : enrollments.length === 0 ? (
                <div className="bg-slate-50 rounded-2xl p-12 text-center">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-slate-900 mb-2">No courses yet</h3>
                  <p className="text-slate-600 mb-6">Start your learning journey by enrolling in a course.</p>
                  <Link to="/courses">
                    <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full">
                      Explore Courses
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {enrollments.map((enrollment) => (
                    <div
                      key={enrollment.id}
                      className="bg-white rounded-xl border border-slate-100 p-6 hover:shadow-lg transition-all duration-300"
                      data-testid={`enrolled-course-${enrollment.id}`}
                    >
                      <div className="flex flex-col md:flex-row gap-6">
                        <img
                          src={enrollment.course?.image_url}
                          alt={enrollment.course?.title}
                          className="w-full md:w-40 h-32 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <span className="badge badge-lime text-xs mb-2">
                            {enrollment.course?.category}
                          </span>
                          <h3 className="font-heading text-lg font-medium text-slate-900 mb-2">
                            {enrollment.course?.title}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {enrollment.course?.duration}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {enrollment.course?.instructor}
                            </span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Progress</span>
                              <span className="font-medium">{enrollment.progress || 0}%</span>
                            </div>
                            <Progress value={enrollment.progress || 0} className="h-2" />
                          </div>
                          <Button
                            className="mt-4 bg-slate-900 text-white hover:bg-slate-800 rounded-full"
                            size="sm"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Continue Learning
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* AI Assistant Card */}
              <div className="bg-slate-900 text-white rounded-2xl p-6" data-testid="ai-assistant-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="font-medium">AI Career Assistant</h3>
                    <p className="text-sm text-slate-400">Get personalized advice</p>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-4">
                  Chat with our AI assistant for career guidance, course recommendations, and more.
                </p>
                <Link to="/ai-assistant">
                  <Button className="w-full bg-lime-500 text-slate-900 hover:bg-lime-400 rounded-full">
                    Start Chat
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Link
                    to="/counseling"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Book Counseling</p>
                      <p className="text-sm text-slate-500">Schedule a session</p>
                    </div>
                  </Link>
                  <Link
                    to="/courses"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Explore Courses</p>
                      <p className="text-sm text-slate-500">Find new skills</p>
                    </div>
                  </Link>
                  <Link
                    to="/blog"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Read Blog</p>
                      <p className="text-sm text-slate-500">Career tips & insights</p>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DashboardPage;
